








































































































































































import { Vue, Component, Prop } from 'vue-property-decorator';
import { BACKEND_URL } from '@/helpers/constants';
import Booking from '@/models/Booking';
import get from 'lodash/get';
import { saveAs } from 'file-saver';
import {
    getBookingDocumentPrescriber,
    getBookingDocumentAdmin,
    postBookingDocumentAdmin,
    deleteBookingDocumentAdmin,
    postBookingDocumentPrescriber,
    deleteBookingDocumentPrescriber,
    patchBookingBackofficeComment,
} from '@/api/api';
import {
    formatBookingDocumentsTypes,
    formatBookingDocumentsTypesFileName,
} from '@/data/bookingDocumentsTypes';
import PdfThumbnail from '@/components/prescriber/PdfThumbnail.vue';
import UploadComponent, {
    InputFile,
} from '@/components/common/UploadComponent.vue';
import moment from 'moment';
import AuthModule from '@/store/AuthModule';

interface Loaders { [index: string]: boolean; }
interface Files   { [index: string]: InputFile | null; }

@Component({
    components: { PdfThumbnail, UploadComponent },
})
export default class BookingPrivateDocuments extends Vue {
    @Prop() booking!: Booking | undefined;
    @Prop({ type: Boolean, default: false }) asAdmin!: boolean;
    BACKEND_URL = BACKEND_URL;

    loaders: Loaders = { invoiceRearMargin: false };
    files: Files = { invoiceRearMargin: null };

    // Stocké en privé côté backend (var/private/uploads) : pas d'URL statique possible,
    // il faut passer par l'API authentifiée pour la preview et l'ouverture.
    invoiceRearMarginPreviewUrl: string = '';

    // Commentaire BackOffice (interne, jamais visible côté prescripteur)
    commentPopupVisible: boolean = false;
    commentEditing: boolean = false;
    commentText: string = '';
    commentDraft: string = '';
    commentSaving: boolean = false;

    mounted() {
        if (!this.booking) return;
        if (this.booking.invoice_rear_margin) {
            this.files.invoiceRearMargin = {
                filename: `${this.booking.invoice_rear_margin.name}.${this.booking.invoice_rear_margin.extension}`,
                data: '',
            };
            if (!this.isArchiveExtension(this.booking.invoice_rear_margin.extension)) {
                this.loadInvoiceRearMarginPreview();
            }
        }
    }

    // Seuls le PDF et les images se prévisualisent correctement dans un <iframe>/<img>.
    // Une extension inconnue ou absente (URL sans extension, métadonnée manquante) ne veut
    // pas dire "pas d'archive" : le fichier réel peut très bien être un zip/rar. On traite
    // donc par défaut toute extension non reconnue comme une archive, pour ne jamais tenter
    // de la charger dans un iframe et déclencher un téléchargement forcé côté navigateur.
    isArchiveExtension(extension: string | null | undefined): boolean {
        const previewable = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
        return !previewable.includes((extension || '').toLowerCase());
    }

    beforeDestroy() {
        if (this.invoiceRearMarginPreviewUrl) {
            URL.revokeObjectURL(this.invoiceRearMarginPreviewUrl);
        }
    }

    async loadInvoiceRearMarginPreview() {
        if (!this.booking || !this.booking.id) return;
        try {
            const fn = this.asAdmin ? getBookingDocumentAdmin : getBookingDocumentPrescriber;
            const { data } = await fn(this.booking.id, 'invoiceRearMargin');
            this.invoiceRearMarginPreviewUrl = URL.createObjectURL(data);
        } catch (e) {
            // pas de preview possible, la carte retombe sur l'icône générique
        }
    }

    openInTab(url: string) {
        window.open(url, '_blank', 'noopener');
    }

    async openInvoiceRearMargin() {
        if (this.invoiceRearMarginPreviewUrl) {
            this.openInTab(this.invoiceRearMarginPreviewUrl);
            return;
        }
        if (!this.booking || !this.booking.id) return;
        const fn = this.asAdmin ? getBookingDocumentAdmin : getBookingDocumentPrescriber;
        const { data } = await fn(this.booking.id, 'invoiceRearMargin');
        this.invoiceRearMarginPreviewUrl = URL.createObjectURL(data);
        this.openInTab(this.invoiceRearMarginPreviewUrl);
    }

    async downloadBookingDocument(type: string) {
        if (!this.booking || !this.booking.id) return;
        const fn = this.asAdmin ? getBookingDocumentAdmin : getBookingDocumentPrescriber;
        const { data } = await fn(this.booking.id, type);
        saveAs(
            data,
            `${formatBookingDocumentsTypesFileName(
                this.booking.code ? this.booking.code : '',
                type
            )}.${data.type.endsWith('zip') ? 'zip' : 'pdf'}`
        );
    }

    async onFile(file: InputFile, type: string) {
        if (!this.booking || !this.booking.id) return;
        try {
            this.loaders[type] = true;
            if (this.asAdmin) {
                await postBookingDocumentAdmin(this.booking.id, type, {
                    file_name: file.filename,
                    document: file.data,
                });
            } else {
                await postBookingDocumentPrescriber(this.booking.id, type, {
                    file_name: file.filename,
                    document: file.data,
                });
            }
            this.showSuccess('Fichier ajouté');
        } catch (e) {
            this.files[type] = null;
            this.showError(e as Error, "Erreur lors de l'envoi");
        } finally {
            this.loaders[type] = false;
        }
    }

    async onDelete(type: string) {
        if (!this.booking || !this.booking.id) return;
        const fn = this.asAdmin
            ? () => deleteBookingDocumentAdmin(this.booking!.id!, type, '(AUCUN)')
            : () => deleteBookingDocumentPrescriber(this.booking!.id!, type);
        fn()
            .then(() => { this.showSuccess('Document supprimé'); this.files[type] = null; })
            .catch((e: Error) => this.showError(e, 'Erreur lors de la suppression'));
    }

    showSuccess(message: string) {
        const $vs = (this as any).$vs;
        $vs.notify({ color: 'success', position: 'top-right', title: 'Succès', text: message });
    }

    showError(error: Error, defaultMessage: string) {
        const message = get(error, 'response.data.message', defaultMessage);
        const $vs = (this as any).$vs;
        $vs.notify({ color: 'danger', position: 'top-right', title: 'Erreur', text: message });
    }

    // ── Commentaire BackOffice ──────────────────────────────────────────
    truncateComment(text: string): string {
        if (!text) {
            return '';
        }

        const firstLine = text.split('\n')[0].trim();
        const truncated = firstLine.length > 20 ? firstLine.substring(0, 20).trim() : firstLine;

        return truncated.length < text.length ? `${truncated}…` : truncated;
    }

    openCommentPopup() {
        this.commentText = (this.booking && this.booking.backoffice_comment) || '';
        this.commentDraft = this.commentText;
        this.commentEditing = false;
        this.commentPopupVisible = true;
    }

    startEditComment() {
        this.commentDraft = this.commentText;
        this.commentEditing = true;
    }

    cancelEditComment() {
        this.commentDraft = this.commentText;
        this.commentEditing = false;
    }

    // Attribue le commentaire à son auteur : premier écrit = "Commentaire de X le date",
    // modification d'un commentaire existant = ajout d'une ligne "X a modifié ce commentaire le date"
    private buildAttributedComment(original: string, draft: string): string {
        const userName = AuthModule.firstname || '';
        const userLastName = AuthModule.lastname || '';
        const fullName = `${userName} ${userLastName}`.trim();
        const currentDate = new Date().toLocaleDateString('fr-FR');

        if (!original.trim()) {
            return `Commentaire de ${fullName || 'Un utilisateur'} le ${currentDate} :\n${draft.trim()}\n\n`;
        }

        if (draft === original) {
            return draft;
        }

        let finalText = draft;
        if (finalText.trim() && !finalText.endsWith('\n')) {
            finalText += '\n';
        }
        finalText += `${fullName || 'Un utilisateur'} a modifié ce commentaire le ${currentDate}.`;

        return finalText;
    }

    async saveComment() {
        if (!this.booking || !this.booking.id) {
            return;
        }

        this.commentSaving = true;
        try {
            const finalText = this.buildAttributedComment(this.commentText, this.commentDraft);

            await patchBookingBackofficeComment(this.booking.id, finalText);

            this.booking.backoffice_comment = finalText;
            this.commentText = finalText;
            this.commentEditing = false;

            this.showSuccess('Le commentaire a bien été mis à jour');
        } catch (e) {
            this.showError(e as Error, 'Erreur lors de la modification du commentaire');
        } finally {
            this.commentSaving = false;
        }
    }
}
